/**
 * Onboarding gate — lets any descendant open the stepped <OnboardingDialog />
 * when a signed-out user takes an action that needs an account (liking an event,
 * peeking at Kikis, etc.). The primary CTA drops the user into the quiz; the
 * secondary "Sign in" link opens the real sign-in flow.
 *
 * Components reach this through `useOnboardingGate()`, which returns null when
 * no provider is mounted — so shared components (EventCard, KikiCarousel) can
 * fall back to opening the sign-in dialog directly outside the events index.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useIsSignedIn } from "../lib/platform/AuthProvider";
import { OnboardingDialog } from "./OnboardingDialog";
interface OnboardingContextValue {
  /**
   * Open the onboarding intro to gate an action that requires an account.
   * Pass an app path (e.g. an event's `/events/slug`) to send the user back there
   * once they finish signing up and the quiz.
   */
  openOnboarding: (postSignupUrl?: string) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [postSignupUrl, setPostSignupUrl] = useState<string | undefined>(undefined);
  const isSignedIn = useIsSignedIn();

  const openOnboarding = useCallback((url?: string) => {
    setPostSignupUrl(url);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <OnboardingContext.Provider value={{ openOnboarding }}>
      {children}
      <OnboardingDialog
        isOpen={isOpen}
        onClose={close}
        postSignupUrl={postSignupUrl}
        hideAuthActions={isSignedIn}
      />
    </OnboardingContext.Provider>
  );
}

/** Returns the onboarding gate, or null when no provider is mounted. */
export function useOnboardingGate(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}
